import {
    useReducer,
    createContext,
    useEffect,
    useContext,
    useMemo,
    useState,
} from 'react'
import { initializeApp } from 'firebase/app'
import {
    getAuth,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
} from 'firebase/auth'
import { osmosisFirebaseConfig } from '../config/osmosisConfig'
import { useToastMessage } from './ToastPopUpContext'
import { getAuthentication } from '../apiRoutes/authenticationRoutes'
import { useSocket } from './SocketContext'
import { editUser } from '../apiRoutes'
import { useGlobalState } from './GlobalStateContext'

import { Grid } from '../components/UniversalComponents'
import { CircularProgress, Typography } from '@mui/material'

interface ContextValues {
    auth?: any
    setAuth?: (newAuth) => {}
    signIn?: ({ auth, username, password }) => {}
    signOutApp?: (param?: any) => {}
}

interface Preferences {
    boatManagementTable: any[]
}

type Auth = {
    user?: any
    authenticated?: boolean
    preferences?: Preferences
}

type Props = {
    children: React.ReactNode
}

const app = initializeApp(osmosisFirebaseConfig)
export const authConnection = getAuth(app)

export const Authentication = createContext({
    auth: {
        userPreferences: {
            boatManagementTable: [],
            dataTableColumns: {
                devices: {
                    columns: {},
                    density: '',
                },
                engines: {
                    engine: {
                        columns: {},
                        density: '',
                    },
                    engineCalibration: {
                        columns: {},
                        density: '',
                    },
                    engineModel: {
                        columns: {},
                        density: '',
                    },
                    engineManufacturers: {
                        columns: {},
                        density: '',
                    },
                },
                boats: {
                    boatConfig: {
                        columns: {},
                        density: '',
                    },
                    boatModels: {
                        columns: {},
                        density: '',
                    },
                    boatManufacturers: {
                        columns: {},
                        density: '',
                    },
                    boatProvisioning: {
                        columns: {},
                        density: '',
                    },
                    unpairedDeviceBoats: {
                        columns: {},
                        density: '',
                    },
                },
                dealers: {
                    columns: {},
                    density: '',
                },
                signals: {
                    signalManager: {
                        columns: {},
                        density: '',
                    },
                    signalEvents: {
                        columns: {},
                        density: '',
                    },
                    unitOfMeasurement: {
                        columns: {},
                        density: '',
                    },
                    signalProfileManager: {
                        columns: {},
                        density: '',
                    },
                    signalFormulaManager: {
                        columns: {},
                        density: '',
                    },
                },
                subscriptions: {
                    subscriptionProfileManager: {
                        columns: {},
                        density: '',
                    },
                    assignBoatSubscriptions: {
                        columns: {},
                        density: '',
                    },
                },
                system: {
                    osPatchNotes: {
                        columns: {},
                        density: '',
                    },
                    mobilePatchNotes: {
                        columns: {},
                        density: '',
                    },
                },
                users: {
                    userAdmin: {
                        columns: {},
                        density: '',
                    },
                    userNotificationSubscription: {
                        columns: {},
                        density: '',
                    },
                    userRegistration: {
                        columns: {},
                        density: '',
                    },
                },
                supportTicketAdmin: {
                    columns: {},
                    density: '',
                },
                manufacturers: {
                    columns: {},
                    density: '',
                },
            },
        },
        userRole: '',
        userPermissions: {},
        _id: '',
        username: '',
        activeManufacturer: { manufacturerName: '' },
        manufacturer: [],
        authenticated: false,
        loading: true,
        idToken: null,
        user: { activeManufacturer: { _id: '' }, assignedDealer: '' },
        userProfile: {
            superAdmin: false,
            userProfilePermissions: {
                Users: {
                    read: false,
                    write: false,
                },
            },
            dealerProfile: false,
            userProfileAssignedManufacturers: [],
        },
        signingIn: false,
    },

    setAuth: (param) => {},
    signIn: (param) => {},
    signOutApp: (param?: any) => {},
    setUser: (param: any) => {},
    realAuth: {} as any,
    emulation: null as any,
    isEmulating: false,
    readOnly: false,
    setEmulation: (entity: any) => {},
    exitEmulation: () => {},
})
export const AuthenticationContext = ({ children }: Props) => {
    const setToastMessage = useToastMessage()
    const { apiSocket } = useSocket()

    const { setGlobalState } = useGlobalState()

    let defaultState = {
        userPreferences: {},
        userRole: '',
        userPermissions: {},
        activeManufacturer: { manufacturerName: '' },
        manufacturer: [],
        _id: '',
        username: '',
        authenticated: false,
        loading: true,
        idToken: null,
        user: {},
        userProfile: { superAdmin: false },
        signingIn: false,
    }
    const reducer = (oldState: any, newState: any) => ({
        ...oldState,
        ...newState,
    })
    const [state, setState] = useReducer(reducer, defaultState)
    const { authenticated, _id, activeManufacturer } = state

    const setAuth = (newAuth) => {
        setState({ ...newAuth })
    }

    // Osmosis OS is for Osmosis + manufacturers (and dealers) only. Plain
    // customer accounts (UserProfile.role === 'USER') may not sign in to the
    // web app — gate on the populated userProfile role from the authenticate
    // payload at both sign-in and silent re-auth.
    const isBlockedUserRole = (session: any): boolean =>
        String(session?.userProfile?.role || '').toUpperCase() === 'USER'

    const signIn = async ({ username, password }) => {
        try {
            if (authConnection) {
                if (username && password) {
                    const authenticate = await signInWithEmailAndPassword(
                        authConnection,
                        username,
                        password
                    )

                    if (authenticate) {
                        const { user } = authenticate
                        const { uid } = user
                        const currentSession = uid
                            ? await getAuthentication({ uid })
                            : null
                        if (currentSession && currentSession._id) {
                            if (isBlockedUserRole(currentSession)) {
                                await signOut(authConnection)
                                globalThis.username = ''
                                setAuth({ ...defaultState, loading: false })
                                setToastMessage(
                                    "You don't have permission to access this application.",
                                    'error'
                                )
                                return
                            }
                            const idToken = await user.getIdToken()
                            globalThis.username = currentSession.username
                            setAuth({
                                ...currentSession,
                                authenticated: true,
                                loading: false,
                                idToken,
                                signingIn: true,
                            })
                        } else {
                            // Firebase auth succeeded but there is no matching
                            // account — sign out instead of leaving a wedged,
                            // half-authenticated session that would spin forever.
                            await signOut(authConnection)
                            globalThis.username = ''
                            setAuth({ ...defaultState, loading: false })
                            setToastMessage(
                                'No account was found for this login. Please contact support.',
                                'error'
                            )
                        }
                    }
                }
            }
        } catch (err: any) {
            setAuth({ ...state, loading: false })
            setToastMessage('Invalid Username or Password', 'error')
            console.error(err)
        }
    }
    const signOutApp = async () => {
        try {
            await signOut(authConnection)
            setGlobalState({ defaultState: {} })
            setAuth({ ...defaultState, loading: false })
        } catch (err) {
            console.error('Sign-out error:', err)
            setAuth({ ...defaultState, loading: false })
        }
    }

    interface SetUserParams {
        key: string
        value: any
        type?: string
    }

    const setUser = async ({ key, value, type }: SetUserParams) => {
        try {
            let parsedFormData = {}
            if (!type) {
                parsedFormData = { id: _id, ...state.user, [key]: value }
            } else if (type === 'columnData') {
                parsedFormData = {
                    id: _id,
                    ...state.user,
                    userPreferences: {
                        ...state.user.userPreferences,
                        dataTableColumns: {
                            ...state.user.userPreferences?.dataTableColumns,
                            [key]: {
                                ...state.user.userPreferences
                                    .dataTableColumns?.[key],
                                ...value,
                            },
                        },
                    },
                }
            }

            let res: any

            res = await editUser(parsedFormData)
            if (res) {
                const { data, status } = res

                if (data && status === 200) {
                    setToastMessage(
                        `User: ${data.userEmail} preferences were successfully edited`,
                        'success'
                    )
                    // getData()
                } else {
                    throw new Error('User edit was unsuccessfull')
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err.message}`, 'error')
        }
    }

    const getData = async () => {
        try {
            const currentSession = await getAuthentication({
                _id,
            })

            if (currentSession) {
                setAuth({
                    ...currentSession,
                    authenticated: true,
                })
            } else {
                throw new Error('No user found')
            }
        } catch (error) {
            console.error('error')
        }
    }

    useEffect(() => {
        const getLocalAuth = async () => {
            if (authConnection) {
                onAuthStateChanged(authConnection, async (user) => {
                    if (user) {
                        const { uid } = user
                        let currentSession: any
                        try {
                            currentSession = uid
                                ? await getAuthentication({ uid })
                                : null
                        } catch {
                            currentSession = null
                        }
                        // A valid backend session carries an _id. If the Firebase
                        // account has no matching User (deleted, re-keyed, or a
                        // mobile-only account hitting the web app), sign out so the
                        // app never spins forever on a session it can't resolve.
                        if (currentSession && currentSession._id) {
                            if (isBlockedUserRole(currentSession)) {
                                await signOut(authConnection)
                                globalThis.username = ''
                                setAuth({
                                    ...defaultState,
                                    authenticated: false,
                                    loading: false,
                                })
                                setToastMessage(
                                    "You don't have permission to access this application.",
                                    'error'
                                )
                                return
                            }
                            const idToken = await user.getIdToken()
                            globalThis.username = currentSession.username
                            setAuth({
                                ...currentSession,
                                authenticated: true,
                                loading: false,
                                idToken,
                            })
                        } else {
                            await signOut(authConnection)
                            globalThis.username = ''
                            setAuth({
                                ...defaultState,
                                authenticated: false,
                                loading: false,
                            })
                            setToastMessage(
                                'No account was found for this login. Please contact support.',
                                'error'
                            )
                        }
                    } else {
                        globalThis.username = ''
                        setAuth({
                            ...defaultState,
                            authenticated: false,
                            loading: false,
                        })
                    }
                })
            }
        }
        getLocalAuth()
    }, [])

    // useEffect(() => {
    //     if (authenticated && _id) {
    //         apiSocket?.on(_id, () => {
    //             getData()
    //         })
    //     }
    // }, [authenticated, _id])

    // --- God mode: super-admin emulation of a manufacturer / dealer profile ---
    const isRealSuperAdmin = state?.userProfile?.superAdmin === true
    const [emulation, setEmulationState] = useState<any>(() => {
        try {
            const raw = localStorage.getItem('osmosisEmulation')
            return raw ? JSON.parse(raw) : null
        } catch {
            return null
        }
    })

    const isEmulating = !!emulation && isRealSuperAdmin

    // Expose a global emulation token so the API layer (buildRoute) + sockets can
    // attach it to every request — the backend honors it ONLY when the real
    // caller is a super admin. Also persist so it survives a refresh.
    // Persist the emulation CHOICE across refreshes (independent of auth-loading
    // state, so it survives a reload before auth resolves).
    useEffect(() => {
        try {
            if (emulation)
                localStorage.setItem(
                    'osmosisEmulation',
                    JSON.stringify(emulation)
                )
            else localStorage.removeItem('osmosisEmulation')
        } catch {
            /* storage unavailable */
        }
    }, [emulation])

    // Expose the token only when ACTUALLY emulating (real super admin confirmed).
    useEffect(() => {
        const g: any = globalThis
        g.osmosisEmulate =
            isEmulating && emulation
                ? {
                      type: emulation.type,
                      id: emulation.id,
                      // dealer profile carries its dealers (+ manufacturers) for
                      // backend $in scoping of dealer- and manufacturer-keyed data
                      dealers: emulation.dealers,
                      manufacturers: emulation.manufacturers,
                  }
                : null
    }, [isEmulating, emulation])

    // Drop emulation only on a real logout — NOT during the initial auth-loading
    // window (where authenticated is briefly false).
    useEffect(() => {
        if (!state.loading && !authenticated && emulation) {
            setEmulationState(null)
        }
    }, [authenticated, state.loading]) // eslint-disable-line

    const setEmulation = (entity: any) => {
        // entity: { type:'manufacturer'|'dealer', id, name, manufacturer? }
        if (!isRealSuperAdmin || !entity?.id) return
        setEmulationState(entity)
    }
    const exitEmulation = () => setEmulationState(null)

    // Effective auth the rest of the app sees while emulating: the chosen tier
    // (NOT super admin), scoped to the emulated entity, and read-only.
    const effectiveAuth = useMemo(() => {
        if (!isEmulating) return state
        const isDealer = emulation.type === 'dealer'
        // Manufacturer impersonation scopes to the chosen manufacturer; many
        // admin modules read activeManufacturer from `auth`, so set it here too
        // (mirrors the oupUser overlay that drives the dashboard).
        const emulatedManufacturer =
            emulation.type === 'manufacturer'
                ? { _id: emulation.id, manufacturerName: emulation.name }
                : state.activeManufacturer
        return {
            ...state,
            emulating: true,
            readOnly: true,
            userRole: isDealer ? 'DEALER' : 'MANUFACTURER',
            activeManufacturer: emulatedManufacturer,
            userProfile: {
                ...state.userProfile,
                superAdmin: false,
                dealerProfile: isDealer,
                mobileCustomerProfile: false,
                role: isDealer ? 'DEALER' : 'MANUFACTURER',
                userProfileAssignedManufacturers: isDealer
                    ? state.userProfile?.userProfileAssignedManufacturers
                    : [emulation.id],
            },
        }
    }, [state, emulation, isEmulating])

    const value = useMemo(
        () => ({
            auth: effectiveAuth,
            realAuth: state,
            setAuth,
            signIn,
            signOutApp,
            setUser,
            emulation: isEmulating ? emulation : null,
            isEmulating,
            readOnly: isEmulating,
            setEmulation,
            exitEmulation,
        }),
        [effectiveAuth, state, isEmulating, emulation]
    )

    return (
        <Authentication.Provider value={value}>
            {children}
        </Authentication.Provider>
    )
}

export const useAuth = () => useContext(Authentication)
export const useEmulation = () => {
    const c = useContext(Authentication) as any
    return {
        emulation: c.emulation,
        isEmulating: c.isEmulating,
        readOnly: c.readOnly,
        realAuth: c.realAuth,
        setEmulation: c.setEmulation,
        exitEmulation: c.exitEmulation,
    }
}
export const usePermissions = () => {
    const currentUser = useContext(Authentication)
    const { userPermissions, userRole, activeManufacturer, userPreferences } =
        currentUser.auth
    return {
        ...userPermissions,
        userRole,
        activeManufacturer,
        ...userPreferences,
    }
}
